import * as React from 'react';
import { cn } from 'lib/utils';
import { Box } from 'components/ui/Box';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/ui/table';
import { Skeleton } from 'components/ui/skeleton';
import { Trash2 } from 'lucide-react';
import { useDeleteTask } from 'hooks/api/service-orders/use-delete-task';
import { useMutation, useQueryClient } from 'react-query';
import { notify } from 'services';
import { ConfirmationDialog, ConfirmationDialogRef } from 'components/ui/confirmation-dialog';

type Props = React.HTMLAttributes<HTMLDivElement> & {
  data?: any[];
  loading?: boolean;
  showDeleteTaskButton?: boolean;
}

export const TasksTable = ({ data, loading, showDeleteTaskButton, className }: Props) => {
  const { deleteTask } = useDeleteTask();
  const queryClient = useQueryClient();

  const confirmationDialogRef = React.useRef<ConfirmationDialogRef>(null);

  const deleteTaskMutation = useMutation({
    mutationFn: deleteTask,
    onSuccess: async (_, variables: any) => {
      notify('success', 'Trabalho excluído com sucesso!');

      await queryClient.invalidateQueries(['fetch-service-order']);
    },
    onError: () => {
      notify('error', 'Erro ao excluir trabalho.');
    },
  });

  const onDelete = React.useCallback(async (taskId: number) => {
    const result = await confirmationDialogRef.current?.openDialog();

    if (result === 'confirmed') {
      deleteTaskMutation.mutate({ taskId });
    }
  }, [deleteTaskMutation]);

  if (!data || loading) {
    return (
      <div className={cn('flex gap-4', className)}>
        <TableSkeleton />
      </div>
    );
  }

  return (
    <div className={cn('', className)}>
      <ConfirmationDialog
        ref={confirmationDialogRef}
        title="Confirmação"
        message="Tem certeza que deseja excluir este trabalho?"
        confirmationButtonLabel="Excluir"
        cancelButtonLabel="Cancelar"
        confirmationButtonProps={{ variant: 'destructive' }}
      />
      <Box>
        <Table>
          <TableHeader>
            <TableRow className="font-GilroySemibold text-sm">
              <TableHead className="w-[100px]">ID</TableHead>
              <TableHead>Tipo</TableHead>
              <TableHead className="w-[50px] text-right"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.map((item) => (
              <TableRow key={item.id} className="font-sans text-xs">
                <TableCell>{item.id}</TableCell>
                <TableCell>{item.task}</TableCell>
                <TableCell className="text-right">
                  {showDeleteTaskButton &&
                    <Trash2
                      className="text-gray-200 hover:text-gray-400 transition-all cursor-pointer"
                      onClick={() => {
                        onDelete(item.id);
                      }}
                    />
                  }
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </div>
  );
};

const TableSkeleton = () => {
  return (
    <Box className="w-full">
      <Table>
        <TableHeader>
          <TableRow className="font-GilroySemibold text-sm">
            <TableHead className="w-[100px]">ID</TableHead>
            <TableHead>Tipo</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {[...Array(3)].map((_, index) => (
            <TableRow key={index}>
              <TableCell><Skeleton className="h-4 w-full" /></TableCell>
              <TableCell><Skeleton className="h-4 w-full" /></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};